import { useForm } from "@mantine/form";
import { useDisclosure } from '@mantine/hooks';
import axios from "axios";
import EXIF from "exif-js";
import { getFormattedTimestamp } from "helpers/stringHelpers";
import { reconstructOverlay } from "helpers/gridOverlay";
import { loadSession, saveSession } from "helpers/persistence";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';

import { Photo } from "types/Photo";


export default function usePhotoProcessor() {

    const navigate = useNavigate();

    const cameraInputRef = useRef<HTMLInputElement>(null);  // Reference to the hidden file input
    const galleryInputRef = useRef<HTMLInputElement>(null);  // Reference to the hidden file input
    const [photos, setPhotos] = useState<Photo[]>([]);
    // Monotonic id source so ids stay unique even when several files are added
    // in the same tick (e.g. a multi-file drag-and-drop).
    const photoIdCounter = useRef(1);

    // Restore a persisted session (inspectionID + form) synchronously so the
    // form seeds without a flash. Photos are re-fetched from the backend below.
    const [savedSession] = useState(() => loadSession());

    const [inspectionID, setInspectionID] = useState(savedSession?.inspectionID ?? uuidv4())
    const form = useForm({
        initialValues: savedSession?.form ?? {
            user: '',
            assetCode: '',
            baselineWidth: '1.00',
            location: '',
            comments: ''
        },
        validate: {
            user: (value: string) => (value.length < 2 ? 'Name must be at least 2 characters' : null),
            assetCode: (value: string) => (value.length < 2 ? 'Asset code must be at least 2 characters' : null),
            location: (value: string) => (value.length < 2 ? 'Location must be at least 2 characters' : null),
        },
    });

    const bugReportForm = useForm({
        initialValues: {
            category: '',
            description: ''
        }
    });


    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState<boolean>(false);
    const [photoModalCurrentPhoto, setPhotoModalCurrentPhoto] = useState<any>(null);
    const [photoOpened, { open: photoOpen, close: photoClose }] = useDisclosure(false);
    const [confirmationOpened, { open: confirmationOpen, close: confirmationClose }] = useDisclosure(false);

    const processImage = async (file: string | Blob, photoId: string) => {
        const formData = new FormData();
        formData.append("image", file);
        const details = {
            ...form.getValues(),
            'inspectionID': inspectionID,
            'timestamp': getFormattedTimestamp(),
        };
        formData.append("details", JSON.stringify(details))

        try {
            console.log("Sending Image to api...")
            const response = await axios({
                method: 'post',
                url: `${process.env.REACT_APP_API_URL}/process-image`,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log("Response Received!")
            if (response.data.error) {
                console.error('Error processing the image: ', response.data.msg);
                setPhotos((prevPhotos) =>
                    prevPhotos.map((photo) =>
                        photo.id.toString() === photoId
                            ? {
                                ...photo,
                                label: `Could not find Screen`,
                                state: "error",
                            }
                            : photo
                    )
                );

            }
            else {
                const byteCharacters = atob(response.data.image);
                const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "image/jpeg" });
                const imageUrl = URL.createObjectURL(blob);
                setPhotos((prevPhotos) =>
                    prevPhotos.map((photo) =>
                        photo.id.toString() === photoId
                            ? {
                                ...photo,
                                label: `Gap Width: ${response.data.measurements.mean.toFixed(2)}mm`,
                                state: "active",
                                annotatedSrc: imageUrl,
                                averageWidth: response.data.measurements.mean,
                                widths: response.data.measurements.widths,
                                geometry: response.data.measurements.geometry,
                            }
                            : photo
                    )
                );
            }
        } catch (error) {
            console.log(error)
            setPhotos((prevPhotos) =>
                prevPhotos.map((photo) =>
                    photo.id.toString() === photoId
                        ? {
                            ...photo,
                            label: `Upload Error`,
                            state: "error",
                        }
                        : photo
                )
            );
        }
    }

    const getOrientationLabel = (file: File): Promise<"portrait" | "landscape" | "unknown"> => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            if (!e.target?.result) return resolve('unknown');
            const orientation = EXIF.readFromBinaryFile(e.target.result)?.Orientation;
            resolve([5, 6, 7, 8].includes(orientation) ? 'portrait' : 'landscape');
          };
          reader.readAsArrayBuffer(file);
        });
      };

    // Core capture path: takes a File/Blob (from the gallery input OR the
    // in-app viewfinder), shows a loading tile, and kicks off processing.
    // Camera captures can pass a known orientation to skip the EXIF read.
    const addPhotoFromFile = async (
        file: File,
        opts?: { orientation?: "portrait" | "landscape" | "unknown" }
    ) => {
        if (!file) return;

        // Generate a new unique ID
        const newPhotoId = photoIdCounter.current++;
        const newPhotoURL = URL.createObjectURL(file); // Create a temporary URL for the file

        const orientationLabel = opts?.orientation ?? await getOrientationLabel(file);
        const newPhoto: Photo = {
            id: newPhotoId,
            src: newPhotoURL,
            label: `Processing...`,
            state: "loading",
            annotatedSrc: newPhotoURL,
            orientation: orientationLabel,
        };

        setPhotos((prevPhotos) => [...prevPhotos, newPhoto]); // Add the new photo to the state

        processImage(file, newPhotoId.toString());
    };

    // Thin wrapper for the native gallery/file <input onChange>. Supports
    // multi-select — every chosen image is added.
    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        if (files.length === 0) return;
        for (const file of files) {
            if (file.type.startsWith("image/")) {
                await addPhotoFromFile(file);
            }
        }
        event.target.value = ""; // allow re-selecting the same file(s)
    };

    const handleSubmit = async () => {
        // TODO Check if fields are filled out
        const details = {
            ...form.getValues(),
            'inspectionID': inspectionID,
            'status': 'submitted',
        };

        const formData = new FormData();
        // Add all field from details into the formData
        Object.entries(details).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            console.log("Marking data as submitted")
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/update-records`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: false, // Ensure cookies aren't sent if unnecessary
                }
            );

            if (response.data.error) {
                console.error('Error updating records: ', response.data.msg);
            }
            else {
                console.log("Successfully updated records")
                confirmationOpen();
                // navigate("/") // Uncomment to kick user to home screen after submitting
            }
        } catch (error) {
            console.error(error)
        }
    }

    const handleSubmitBugReport = async () => {
        const details = bugReportForm.getValues();
        const formData = new FormData();
        Object.entries(details).forEach(([key, value]) => {
            formData.append(key, value);
        });
        try {
            console.log("Sending bug report")
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/bug-report`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: false, // Ensure cookies aren't sent if unnecessary
                }
            );

            if (response.data.error) {
                console.error('Error sending bug report: ', response.data.msg);
            } else {
                console.log("Bug report sent successfully");
                bugReportForm.reset();
            }
        } catch (error) {
            console.error(error)
        }
    }

    // On mount, if a previous session was restored, re-fetch its photos from the
    // backend (by inspectionID) and rebuild the tiles. The annotated overlay is
    // reconstructed client-side from the raw image + widths + geometry.
    useEffect(() => {
        const restoreId = savedSession?.inspectionID;
        if (!restoreId) return;
        let cancelled = false;

        (async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/inspections/${restoreId}/photos`
                );
                const photosData: any[] = res.data?.photos ?? [];
                const restored: Photo[] = [];

                for (let i = 0; i < photosData.length; i++) {
                    const p = photosData[i];
                    if (!p.rawUrl || !p.widths || !p.geometry) continue;

                    let annotatedSrc: string = p.rawUrl;
                    try {
                        annotatedSrc = await reconstructOverlay(p.rawUrl, p.widths, p.geometry);
                    } catch (e) {
                        console.warn("Overlay reconstruction failed for a restored photo", e);
                    }

                    const orientation: "portrait" | "landscape" =
                        p.geometry.imgHeight > p.geometry.imgWidth ? "portrait" : "landscape";
                    const mean = typeof p.mean === "number" ? p.mean : Number(p.mean);
                    const hasMean = Number.isFinite(mean);

                    restored.push({
                        id: restored.length + 1,
                        measurementID: p.measurementID,
                        src: p.rawUrl,
                        label: hasMean ? `Gap Width: ${mean.toFixed(2)}mm` : "Measured",
                        state: "active",
                        annotatedSrc,
                        orientation,
                        averageWidth: hasMean ? mean : undefined,
                        widths: p.widths,
                        geometry: p.geometry,
                    });
                }

                if (!cancelled && restored.length) {
                    setPhotos(restored);
                    // Continue new ids after the restored ones.
                    photoIdCounter.current = restored.length + 1;
                }
            } catch (e) {
                // Offline / backend unreachable — leave the session empty; the
                // live capture flow still works.
                console.warn("Could not restore session photos", e);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist inspectionID + form fields (debounced) so a refresh keeps them.
    const serializedForm = JSON.stringify(form.getValues());
    useEffect(() => {
        const handle = setTimeout(() => {
            saveSession({ inspectionID, form: form.getValues() });
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serializedForm, inspectionID]);

    // Start a fresh inspection: clear photos + mint a new inspectionID, wipe
    // assetCode + comments, but keep the inspector name, location and baseline.
    const startNewCapture = () => {
        photos.forEach((p) => {
            if (p.src?.startsWith("blob:")) URL.revokeObjectURL(p.src);
            if (p.annotatedSrc?.startsWith("blob:") && p.annotatedSrc !== p.src) {
                URL.revokeObjectURL(p.annotatedSrc);
            }
        });
        setPhotos([]);

        const newInspectionID = uuidv4();
        setInspectionID(newInspectionID);

        const current = form.getValues();
        const keptForm = {
            user: current.user,
            location: current.location,
            baselineWidth: current.baselineWidth,
            assetCode: '',
            comments: '',
        };
        form.setValues(keptForm);
        form.resetDirty(keptForm);

        saveSession({ inspectionID: newInspectionID, form: keptForm });
    };

    return {
        state: {
            photos,
            form,
            bugReportForm,
            location: "St. Ives Mine Site",
            isPhotoModalOpen,
            photoModalCurrentPhoto,
            cameraInputRef,
            galleryInputRef,
            photoOpened,
            confirmationOpened,
        },
        handlers: {
            handlePhotoUpload,
            addPhotoFromFile,
            startNewCapture,
            handleSubmit,
            goHome: () => navigate("/"),
            handleSubmitBugReport,
            openPhotoModal: (photo: any) => {
                setPhotoModalCurrentPhoto(photo);
                // setIsPhotoModalOpen(true);
                photoOpen();
                console.log({ photo });

            },
            modalHandlers: {
                onClose: () => setIsPhotoModalOpen(false),
                onButtonClick: () => {
                    setIsPhotoModalOpen(false)
                    // Release the local object URLs so deleted photos don't leak memory.
                    if (photoModalCurrentPhoto?.src?.startsWith("blob:")) {
                        URL.revokeObjectURL(photoModalCurrentPhoto.src);
                    }
                    if (
                        photoModalCurrentPhoto?.annotatedSrc?.startsWith("blob:") &&
                        photoModalCurrentPhoto.annotatedSrc !== photoModalCurrentPhoto.src
                    ) {
                        URL.revokeObjectURL(photoModalCurrentPhoto.annotatedSrc);
                    }
                    setPhotos(p => p.filter(photo => photo.id !== photoModalCurrentPhoto.id))
                    photoClose();
                },
                photoOpen,
                photoClose,
            },
            confirmationModalHandlers: {
                confirmationOpen,
                confirmationClose,
                onButtonClick: () => {
                    const adminUrl = process.env.REACT_APP_ADMIN_URL || 'https://screen-ai-admin.techt.app';
                    window.open(`${adminUrl}/view/${inspectionID}`, '_blank')
                }
            }
        }
    }
}